import { MatGoogleMapsAutocompleteModule } from '@angular-material-extensions/google-maps-autocomplete';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { getApiUrl } from '@app/core/services/utils/util.service';
import { FronteggAppModule, FronteggAppService, FronteggAuthService } from '@frontegg/angular';
import { UnleashClientModule } from '@lib/unleash';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { DeletedOrdersModule } from '@pages/deleted-orders/deleted-orders.module';
import { ShopService } from '@services/shops/shop.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BaseHttpInterceptor } from './core/interceptors/base-http.interceptor';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { FeatureFlagService } from './core/services/feature-flags/feature-flag.service';
import { PosthogService } from './core/services/posthog/posthog.service';
import { ApiService } from './core/services/api/api.service';
import { DashboardModule } from './dashboard/dashboard.module';
import { FronteggLocalization, fronteggThemeOptions } from './features/frontegg-theme/frontegg-theme';
import { ExtraBoxDialogComponent } from './features/side-panel/components/extra-box-dialog/extra-box-dialog.component';
import { RecommendedPackageDialogComponent } from './features/side-panel/components/recommended-package-dialog/recommended-package-dialog.component';
import { PagesModule } from './pages/pages.module';
import { AssignModule } from './pages/workspace/assign/assign.module';
import { PreviewModule } from './pages/workspace/verify/preview.module';
import { ExternalToolsModule } from './services/external-tools/external-tools.module';
import { SharedModule } from './sharedModule/shared.module';
import { StorybookModule } from './storybook/storybook.module';
import { TracingInterceptor } from './core/interceptors/tracing.interceptor';
import { MaintenancePageComponent } from './sharedModule/components/maintenance-page/maintenance-page.component';
import { ComparePlansComponent } from './pages/compare-plans/compare-plans.component';

const BACKGROUND_URL = 'assets/images/login/background.png';

@NgModule({
  declarations: [
    AppComponent,
    ExtraBoxDialogComponent,
    RecommendedPackageDialogComponent,
    MaintenancePageComponent,
    ComparePlansComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    AppRoutingModule,
    AssignModule,
    DashboardModule,
    StorybookModule,
    SharedModule,
    PortalModule,
    PagesModule,
    DeletedOrdersModule,
    ScrollingModule,
    PreviewModule,
    MatSnackBarModule,
    ExternalToolsModule,
    FronteggAppModule.forRoot({
      contextOptions: {
        baseUrl: getApiUrl(),
      },
      authOptions: { enableSessionPerTenant: true },
      backgroundImage: `url(${BACKGROUND_URL}) no-repeat right bottom / cover`,
      themeOptions: fronteggThemeOptions,
      customLoader: true,
      localizations: FronteggLocalization,
    }),
    StoreModule.forRoot({}),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    EffectsModule.forRoot([]),
    AppRoutingModule,
    MatGoogleMapsAutocompleteModule.forRoot(environment.GOOGLE_API_KEY),
    UnleashClientModule.forRoot({
      url: environment.UNLEASH_PROXY_URL,
      clientKey: environment.UNLEASH_PROXY_CLIENT_KEY,
      appName: 'main',
    }),
  ],
  providers: [
    /** Load shop before the routing is initialized */
    provideHttpClient(withInterceptorsFromDi()),
    ApiService,
    CookieService,
    ShopService,
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BaseHttpInterceptor,
      multi: true,
    },
    { provide: AnalyticsService, useExisting: PosthogService },
    { provide: FeatureFlagService, useExisting: PosthogService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TracingInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
